/**
 * convertRig — loaded Three scene graph → RigDescriptor (ADR-008 §2, Convert
 * stage). Pure extraction: no mapping (ADR-004's job), no convention sniffing,
 * every liberty in the RigImportReport. World bind comes from inverse-bind
 * matrices ONLY (ADR-003 §2b); the local chain is folded and cross-checked,
 * never trusted.
 * Depends on: three, @harryhausen/kernel. Depended on by: app/import.
 */
import { Bone, Matrix4, Object3D, Quaternion, SkinnedMesh, Vector3 } from 'three';
import {
  detectReflectedPairs,
  parseRigDescriptor,
  quatAngleBetween,
  quatMultiply,
  quatRotateVec3,
  vec3Add,
  vec3Length,
  vec3Sub,
  QUAT_IDENTITY,
  type Quat,
  type RigDescriptor,
  type Vec3Data,
} from '@harryhausen/kernel';
import {
  ImproperBindFrameError,
  InconsistentBindError,
  MultipleSkeletonsError,
  NonDecomposableBindError,
  NoSkeletonError,
} from './errors.js';
import type { ConventionDeclaration, RigImportReport, TrackSpaceTransform } from './import-report.js';

export interface ConvertRigOptions {
  /** Human-readable rig identity (file name) for the descriptor and errors. */
  readonly label: string;
  /** Version of the ontology the orchestrator has loaded (stamped, not guessed). */
  readonly ontologyVersion: string;
  /** Declared conventions with provenance — from the Load stage, never sniffed here. */
  readonly convention: ConventionDeclaration;
  /** Authored (file) name lookup for rename reporting (ADR-008 §2j). */
  readonly authoredNameFor?: (object: Object3D) => string | undefined;
  /** Skeleton choice when the scene has several — by SkinnedMesh name or root bone name. */
  readonly selectSkeleton?: string;
  /** Platform provenance from file metadata, forwarded into the report. */
  readonly generator?: string | null;
  /** Extensions the loader did not handle, forwarded into the report. */
  readonly unhandledExtensions?: readonly string[];
  /** Divergence report thresholds (fractions of rig extent / radians). */
  readonly divergenceTolerance?: { readonly position?: number; readonly rotationRadians?: number };
}

export interface ConvertRigResult {
  readonly rig: RigDescriptor;
  readonly report: RigImportReport;
  /**
   * Per-bone interposed (non-bone ancestor) transforms — the SAME transforms
   * folded into localBind here. Feed to convertClip so animated translation
   * shares worldBind space (ADR-008 §2b). Empty unless a non-bone ancestor
   * (e.g. a scaled Armature) sits above a bone.
   */
  readonly trackNormalization: readonly TrackSpaceTransform[];
}

interface Trs {
  readonly position: Vec3Data;
  readonly rotation: Quat;
  readonly scale: Vec3Data;
}

/** Faithfulness gate: a matrix that does not survive TRS round-trip has shear (ADR-008 §7). */
function decomposeStrict(matrix: Matrix4, boneName: string): Trs {
  const p = new Vector3();
  const q = new Quaternion();
  const s = new Vector3();
  matrix.decompose(p, q, s);
  const recomposed = new Matrix4().compose(p, q, s);
  let scaleRef = 0;
  for (const e of matrix.elements) scaleRef = Math.max(scaleRef, Math.abs(e));
  let maxError = 0;
  for (let i = 0; i < 16; i += 1) {
    maxError = Math.max(maxError, Math.abs((matrix.elements[i] as number) - (recomposed.elements[i] as number)));
  }
  if (maxError > 1e-4 * Math.max(1, scaleRef)) {
    throw new NonDecomposableBindError(boneName, maxError);
  }
  return {
    position: { x: p.x, y: p.y, z: p.z },
    rotation: { x: q.x, y: q.y, z: q.z, w: q.w },
    scale: { x: s.x, y: s.y, z: s.z },
  };
}

/** Compose parent ∘ child in TRS space (uniform-enough scales; divergence report audits the liberty). */
function trsCompose(parent: Trs, child: Trs): Trs {
  const scaled: Vec3Data = {
    x: child.position.x * parent.scale.x,
    y: child.position.y * parent.scale.y,
    z: child.position.z * parent.scale.z,
  };
  return {
    position: vec3Add(parent.position, quatRotateVec3(parent.rotation, scaled)),
    rotation: quatMultiply(parent.rotation, child.rotation),
    scale: {
      x: parent.scale.x * child.scale.x,
      y: parent.scale.y * child.scale.y,
      z: parent.scale.z * child.scale.z,
    },
  };
}

interface DiscoveredSkeleton {
  readonly bones: Object3D[];
  readonly worldBinds: Map<Object3D, Trs>;
  readonly bindSource: 'ibm' | 'rest-pose';
}

/** Is this node a Three Bone (avoids importing Bone just for the guard)? */
function isBone(node: Object3D): boolean {
  return (node as { isBone?: boolean }).isBone === true;
}

/**
 * Find the skeleton and its world binds. PREFERRED: a skinned mesh's skeleton,
 * world bind from inverse-bind matrices (ADR-003 §2b — the only source with an
 * independent cross-check). FALLBACK (ADR-003 §12): a mesh-less bone hierarchy
 * (animation-only FBX / BVH), world bind from the loader-resolved rest-pose
 * world matrices — pre-rotations already baked in, NOT the deprecated naive
 * chaining. `NoSkeletonError` only when neither exists.
 */
/**
 * The skinned meshes of the SELECTED skeleton — the ONE selection rule, as a
 * pure function of a scene. The Document run parses a file twice (once for
 * the rig, once for the view), so bone uuids never survive from one parse
 * to the other; what does survive is this rule: skeletons are distinguished
 * by bone OBJECT within a parse (a duplicated skeleton carries the very same
 * names — mixamo-walking's two X Bot skins), and the choice between them is
 * the declared `selectSkeleton` (mesh name or root-bone name).
 *
 * Membership is by bone-object OVERLAP, not exact skeleton equality: a
 * garment's Skeleton often holds only the SUBSET of the body's bones it
 * references (Codex P2, #108 round two), and a subset skin is the same
 * skeleton's flesh — skipping it would leave clothing deforming differently
 * from the corrected body. Meshes sharing ANY bone object group together;
 * DISJOINT groups are distinct skeletons and require the selector. The
 * returned list is sorted largest-skeleton-first, so the first mesh's
 * Skeleton covers the group's bind (the body, not a cuff). A door that must
 * act only on meshes skinned to the selected rig calls this on the scene it
 * acts on. Empty for mesh-less sources.
 */
export function skinnedMeshesOfSelectedSkeleton(
  scene: Object3D,
  options: Pick<ConvertRigOptions, 'label' | 'selectSkeleton'>,
): SkinnedMesh[] {
  const all: SkinnedMesh[] = [];
  scene.traverse((node) => {
    if (node instanceof SkinnedMesh && node.skeleton && node.skeleton.bones.length > 0) all.push(node);
  });
  if (all.length === 0) return [];
  // Connected components over shared bone objects.
  const groups: { meshes: SkinnedMesh[]; uuids: Set<string> }[] = [];
  for (const mesh of all) {
    const uuids = new Set(mesh.skeleton.bones.map((b) => b.uuid));
    const touching = groups.filter((g) => [...uuids].some((u) => g.uuids.has(u)));
    if (touching.length === 0) {
      groups.push({ meshes: [mesh], uuids });
      continue;
    }
    const [home, ...rest] = touching;
    home!.meshes.push(mesh);
    for (const u of uuids) home!.uuids.add(u);
    for (const g of rest) {
      home!.meshes.push(...g.meshes);
      for (const u of g.uuids) home!.uuids.add(u);
      groups.splice(groups.indexOf(g), 1);
    }
  }
  let chosen = groups;
  if (groups.length > 1) {
    const bySelector = groups.filter((g) =>
      g.meshes.some((mesh) => mesh.name === options.selectSkeleton || mesh.skeleton.bones[0]?.name === options.selectSkeleton),
    );
    if (bySelector.length !== 1) {
      throw new MultipleSkeletonsError(
        options.label,
        groups.map((g) => g.meshes[0]!.name || g.meshes[0]!.skeleton.bones[0]?.name || '(unnamed)'),
      );
    }
    chosen = bySelector;
  }
  return [...chosen[0]!.meshes].sort((a, b) => b.skeleton.bones.length - a.skeleton.bones.length);
}

/**
 * A bone BY NAME inside the selected skeleton — never `getObjectByName` on
 * the root, which returns whichever same-named bone traversal meets first
 * when a target carries two disjoint skeletons with duplicate joint names
 * (Codex P1, #126 round three). Every door that acts on one named bone of
 * the Document's rig resolves it here: the reseat, the seating marker.
 * Undefined when no skinned mesh of the selected group binds that name.
 */
export function boneOfSelectedSkeleton(
  scene: Object3D,
  boneName: string,
  options: Pick<ConvertRigOptions, 'label' | 'selectSkeleton'>,
): Bone | undefined {
  return skinnedMeshesOfSelectedSkeleton(scene, options)
    .map((mesh) => mesh.skeleton.bones.find((b) => b.name === boneName))
    .find((b): b is Bone => b !== undefined);
}

/**
 * How two binds of one bone disagree, per component in its own units — or
 * null when they agree. Translation: 1e-4 of its magnitude (never below
 * 1e-6); rotation: 0.05° between the two frames; scale: 1e-4 relative.
 */
function bindDisagreement(a: Trs, b: Trs): string | null {
  // Tolerances are SYMMETRIC in the two operands (normalised by the larger
  // magnitude), so acceptance cannot depend on which skin was walked first
  // (Codex P2, #108: 1 vs 1.000100005 must agree or disagree in both orders).
  const dp = Math.hypot(a.position.x - b.position.x, a.position.y - b.position.y, a.position.z - b.position.z);
  const mag = Math.max(1e-6, Math.hypot(a.position.x, a.position.y, a.position.z), Math.hypot(b.position.x, b.position.y, b.position.z));
  if (dp > 1e-4 * Math.max(1, mag)) return `translation differs by ${dp.toExponential(2)}`;
  const dot = Math.min(1, Math.abs(a.rotation.x * b.rotation.x + a.rotation.y * b.rotation.y + a.rotation.z * b.rotation.z + a.rotation.w * b.rotation.w));
  const deg = (2 * Math.acos(dot) * 180) / Math.PI;
  if (deg > 0.05) return `rotation differs by ${deg.toFixed(3)}°`;
  const ds = Math.max(
    Math.abs(a.scale.x - b.scale.x) / Math.max(1e-6, Math.abs(a.scale.x), Math.abs(b.scale.x)),
    Math.abs(a.scale.y - b.scale.y) / Math.max(1e-6, Math.abs(a.scale.y), Math.abs(b.scale.y)),
    Math.abs(a.scale.z - b.scale.z) / Math.max(1e-6, Math.abs(a.scale.z), Math.abs(b.scale.z)),
  );
  if (ds > 1e-4) return `scale differs by ${ds.toExponential(2)} (relative)`;
  return null;
}

function discoverSkeleton(scene: Object3D, options: ConvertRigOptions): DiscoveredSkeleton {
  const selectedMeshes = skinnedMeshesOfSelectedSkeleton(scene, options);
  if (selectedMeshes.length > 0) {
    // The component's bone UNION (Codex P2, #108): overlapping skins need not
    // be strict subsets — a garment can share body bones AND add garment-only
    // helpers — so no single member is guaranteed to hold every bone. Walk the
    // skins largest-first; a bone's first inverse bind wins (the fullest
    // skeleton's), later skins only contribute bones not yet seen.
    const bones: Object3D[] = [];
    const worldBinds = new Map<Object3D, Trs>();
    const seenBinds = new Map<Object3D, { bind: Trs; skin: string }[]>();
    for (const mesh of selectedMeshes) {
      const skin = mesh.name || '(unnamed)';
      mesh.skeleton.bones.forEach((b, i) => {
        const inverse = mesh.skeleton.boneInverses[i] as Matrix4;
        const bind = decomposeStrict(new Matrix4().copy(inverse).invert(), b.name);
        const priors = seenBinds.get(b);
        if (priors !== undefined) {
          // A repeated bone must be bound IDENTICALLY by every skin that
          // shares it — otherwise the rig's bind would depend on walk order
          // (Codex P2, #108). Compared per DECOMPOSED component in its own
          // units (a centimetre-scale translation must not hide a degree),
          // and against EVERY prior bind, not just the first: tolerance
          // equality is not transitive, so first-only would accept a
          // component whose extremes disagree depending on which skin came
          // first (Codex P2, #108 again).
          for (const prior of priors) {
            const disagreement = bindDisagreement(prior.bind, bind);
            if (disagreement !== null) {
              throw new InconsistentBindError(options.label, b.name, [prior.skin, skin], disagreement);
            }
          }
          priors.push({ bind, skin });
          return;
        }
        seenBinds.set(b, [{ bind, skin }]);
        worldBinds.set(b, bind);
        bones.push(b);
      });
    }
    return { bones, worldBinds, bindSource: 'ibm' };
  }

  // Mesh-less: world bind = the loader-resolved rest-pose world matrix.
  scene.updateMatrixWorld(true);
  const allBones: Object3D[] = [];
  scene.traverse((node) => {
    if (isBone(node)) allBones.push(node);
  });
  if (allBones.length === 0) throw new NoSkeletonError(options.label);

  const boneSet = new Set(allBones);
  // A bone is a root iff it has no BONE ancestor. Resolve by nearest-bone
  // ancestry (mirroring convertRig's parent walk below), not the immediate
  // parent — an interposed non-bone node (Root(Bone) → Offset(Object3D) →
  // Tip(Bone)) is one skeleton, and the local chain already folds it.
  const nearestBoneAncestor = (b: Object3D): Object3D | null => {
    let a = b.parent;
    while (a !== null && !boneSet.has(a) && a !== scene) a = a.parent;
    return a !== null && boneSet.has(a) ? a : null;
  };
  const roots = allBones.filter((b) => nearestBoneAncestor(b) === null);
  let bones = allBones;
  if (roots.length !== 1) {
    // Several disjoint bone hierarchies — choose the root explicitly or refuse.
    const selected = roots.filter((r) => r.name === options.selectSkeleton);
    if (selected.length !== 1) {
      throw new MultipleSkeletonsError(options.label, roots.map((r) => r.name || '(unnamed)'));
    }
    const keep = new Set<Object3D>();
    selected[0]!.traverse((n) => {
      if (isBone(n)) keep.add(n);
    });
    bones = allBones.filter((b) => keep.has(b));
  }
  // Express rest-pose world binds in the SCENE frame — the same frame the local
  // chain composes to (convertRig's fold stops at `scene`). Identity when the
  // loader root is untransformed (the common case); when it carries a scale/
  // rotation this keeps worldBind, localBind, and the tracks in one space —
  // there is no divergence audit on this path to catch a mismatch.
  const sceneInverse = new Matrix4().copy(scene.matrixWorld).invert();
  const worldBinds = new Map<Object3D, Trs>();
  for (const b of bones) {
    const worldInScene = new Matrix4().multiplyMatrices(sceneInverse, b.matrixWorld);
    worldBinds.set(b, decomposeStrict(worldInScene, b.name));
  }
  return { bones, worldBinds, bindSource: 'rest-pose' };
}

export function convertRig(scene: Object3D, options: ConvertRigOptions): ConvertRigResult {
  const { bones: skeletonBones, worldBinds, bindSource } = discoverSkeleton(scene, options);
  const boneSet = new Set<Object3D>(skeletonBones);

  // ---- IR hierarchy: nearest bone ancestor; non-bone interposed transforms
  //      fold into localBind so the local chain still composes (ADR-008 §3 note).
  const interposedNodes: Array<{ bone: string; folded: string[] }> = [];
  const renames: Array<{ authored: string; runtime: string }> = [];
  const localBinds = new Map<Object3D, Trs>();
  const interposedTransforms = new Map<Object3D, Trs>();
  const parents = new Map<Object3D, Object3D | null>();

  for (const b of skeletonBones) {
    const folded: string[] = [];
    let ancestor = b.parent;
    const chain: Object3D[] = [];
    while (ancestor !== null && !boneSet.has(ancestor) && ancestor !== scene) {
      chain.push(ancestor);
      ancestor = ancestor.parent;
    }
    const parentBone = ancestor !== null && boneSet.has(ancestor) ? ancestor : null;
    parents.set(b, parentBone);

    // Compose top-down: highest interposed node first. The product of the
    // non-bone ancestors is this bone's track-space normalization — folded
    // into localBind here, and (by convertClip) into the animated translation
    // of the same bone, so the two never disagree on units (ADR-008 §2b).
    const interposed = new Matrix4();
    for (let i = chain.length - 1; i >= 0; i -= 1) {
      const node = chain[i] as Object3D;
      node.updateMatrix();
      interposed.multiply(node.matrix);
      folded.push(node.name || '(unnamed)');
    }
    b.updateMatrix();
    const local = new Matrix4().multiplyMatrices(interposed, b.matrix);
    localBinds.set(b, decomposeStrict(local, b.name));
    if (folded.length > 0) {
      interposedNodes.push({ bone: b.name, folded });
      interposedTransforms.set(b, decomposeStrict(interposed, `${b.name} interposed frame`));
    }

    const authored = options.authoredNameFor?.(b);
    if (authored !== undefined && authored !== b.name) {
      renames.push({ authored, runtime: b.name });
    }
  }

  // ---- Divergence audit (IBM source ONLY): composed local chain vs the
  //      INDEPENDENT IBM-derived world bind (ADR-008 §2b). A rest-pose source
  //      (ADR-003 §12) has a single bind source — the loader-resolved rest pose
  //      — with nothing independent to diverge from, so the audit is skipped;
  //      bindSource:'rest-pose' in the report says so, never a silent pass.
  const divergenceEntries: Array<{ bone: string; positionError: number; rotationErrorRadians: number }> = [];
  let maxPositionError = 0;
  let maxRotationErrorRadians = 0;
  if (bindSource === 'ibm') {
    const composedWorld = new Map<Object3D, Trs>();
    const composeWorld = (b: Object3D): Trs => {
      const cached = composedWorld.get(b);
      if (cached) return cached;
      const local = localBinds.get(b) as Trs;
      const parent = parents.get(b) ?? null;
      const world = parent === null ? local : trsCompose(composeWorld(parent), local);
      composedWorld.set(b, world);
      return world;
    };
    let extent = 0;
    for (const t of worldBinds.values()) extent = Math.max(extent, vec3Length(t.position));
    const posTol = (options.divergenceTolerance?.position ?? 1e-3) * Math.max(extent, 1e-6);
    const rotTol = options.divergenceTolerance?.rotationRadians ?? 1e-3;
    for (const b of skeletonBones) {
      const ibm = worldBinds.get(b) as Trs;
      const chainWorld = composeWorld(b);
      const positionError = vec3Length(vec3Sub(ibm.position, chainWorld.position));
      const rotationErrorRadians = quatAngleBetween(ibm.rotation, chainWorld.rotation);
      maxPositionError = Math.max(maxPositionError, positionError);
      maxRotationErrorRadians = Math.max(maxRotationErrorRadians, rotationErrorRadians);
      if (positionError > posTol || rotationErrorRadians > rotTol) {
        divergenceEntries.push({ bone: b.name, positionError, rotationErrorRadians });
      }
    }
  }

  // ---- Reflection scan (kernel math): the descriptor is born honest (ADR-008 §2e).
  const bones = skeletonBones.map((b) => ({
    name: b.name,
    parent: (parents.get(b) as Object3D | null)?.name ?? null,
    localBind: localBinds.get(b) as Trs,
    worldBind: worldBinds.get(b) as Trs,
  }));
  const scan = detectReflectedPairs(bones, options.convention);
  const declared = new Set(scan.reflectedPairs.flat());
  const undeclarable = scan.improperBones.filter((name) => !declared.has(name));
  if (undeclarable.length > 0) throw new ImproperBindFrameError(options.label, undeclarable);

  // ---- Track-space normalization: expose each bone's interposed transform so
  //      convertClip folds it into that bone's animated translation (ADR-008 §2b).
  const trackNormalization: TrackSpaceTransform[] = skeletonBones
    .filter((b) => interposedTransforms.has(b))
    .map((b) => {
      const t = interposedTransforms.get(b) as Trs;
      return {
        bone: b.name,
        transform: { position: t.position, rotation: t.rotation, scale: t.scale },
        hasRotation: quatAngleBetween(t.rotation, QUAT_IDENTITY) > 1e-6,
      };
    });

  // ---- The kernel validator is the gate (ADR-008 §2, Validate stage).
  const rig = parseRigDescriptor({
    label: options.label,
    ontologyVersion: options.ontologyVersion,
    units: { metersPerUnit: options.convention.metersPerUnit },
    upAxis: options.convention.upAxis,
    facing: options.convention.facing,
    bones,
    reflectedPairs: scan.reflectedPairs.map(([a, b]) => [a, b]),
    mapping: [], // honest: no mapping yet — ADR-004's subsystem is the only producer
  });

  return {
    rig,
    trackNormalization,
    report: {
      convention: options.convention,
      renames,
      bindSource,
      bindDivergence: { maxPositionError, maxRotationErrorRadians, entries: divergenceEntries },
      interposedNodes,
      improperBones: scan.improperBones,
      unpairedSided: scan.unpairedSided,
      mirrorPairs: scan.mirrorPairs,
      generator: options.generator ?? null,
      unhandledExtensions: options.unhandledExtensions ?? [],
    },
  };
}
